import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { TraderLayout } from '../../layouts/TraderLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Receipt,
  Trophy,
  CheckCircle,
  XCircle,
  Clock,
  Ban,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export function MyBets() {
  const [bets, setBets] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('all');
  const [loading, setLoading] = useState(true);
  const { user, refreshUser } = useAuth();
  const { subscribeToType } = useWebSocket();

  const fetchBets = async () => {
    try {
      const response = await axios.get(`${API}/bets/my`);
      setBets(response.data);
    } catch (error) {
      toast.error('Failed to load bets');
    } finally {
      setLoading(false);
    }
  };

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      setRaces(response.data);
    } catch (error) {
      console.error('Failed to load races');
    }
  };

  const handleCancelBet = async (betId) => {
    try {
      await axios.delete(`${API}/bets/${betId}`);
      toast.success('Bet cancelled and stake refunded');
      refreshUser();
      fetchBets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel bet');
    }
  };

  useEffect(() => {
    fetchBets();
    fetchRaces();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToType('*', (data) => {
      if (data.type === 'bet_placed' && data.user_id === user?.id) {
        fetchBets();
      } else if (data.type === 'race_settled') {
        fetchBets();
        fetchRaces();
        refreshUser();
      } else if (data.type === 'bet_cancelled' && data.user_id === user?.id) {
        fetchBets();
        refreshUser();
      }
    });

    return unsubscribe;
  }, [subscribeToType, user, refreshUser]);

  // Filter bets by race
  const filteredBets = selectedRaceId === 'all'
    ? bets
    : bets.filter(b => b.race_id === selectedRaceId);

  const openBets = filteredBets.filter(b => b.status === 'open');
  const settledBets = filteredBets.filter(b => ['won', 'lost'].includes(b.status));
  const cancelledBets = filteredBets.filter(b => b.status === 'cancelled');

  const totalStaked = filteredBets.reduce((sum, b) => sum + b.stake, 0);
  const wonBets = filteredBets.filter(b => b.status === 'won');
  const lostBets = filteredBets.filter(b => b.status === 'lost');
  const totalWon = wonBets.reduce((sum, b) => sum + (b.actual_payout || b.potential_payout), 0);
  const totalLost = lostBets.reduce((sum, b) => sum + b.stake, 0);
  const netProfit = totalWon - totalLost;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'won': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'lost': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'cancelled': return <Ban className="w-4 h-4 text-zinc-400" />;
      default: return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'text-emerald-400 bg-emerald-500/20';
      case 'lost': return 'text-red-400 bg-red-500/20';
      case 'cancelled': return 'text-zinc-400 bg-zinc-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const getBetTypeColor = (betType) => {
    switch (betType) {
      case 'win': return 'bg-blue-500/20 text-blue-400';
      case 'place': return 'bg-emerald-500/20 text-emerald-400';
      case 'ew': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  // Get unique races that have bets
  const racesWithBets = races.filter(r => bets.some(b => b.race_id === r.id));

  const BetCard = ({ bet }) => (
    <div 
      className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 space-y-2"
      data-testid={`bet-row-${bet.id}`}
    >
      {/* Race Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-blue-500" />
          <span className="font-heading text-base font-bold text-zinc-100">{bet.race_venue || 'Unknown'}</span>
          <span className="font-heading text-base font-bold text-blue-400">{bet.race_time || ''}</span>
        </div>
        {bet.status === 'open' ? (
          <button
            onClick={() => handleCancelBet(bet.id)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-bold text-red-400 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 transition-colors"
            data-testid={`cancel-bet-${bet.id}`}
          >
            <Ban className="w-3 h-3" />
            Cancel
          </button>
        ) : (
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(bet.status)}`}>
            {getStatusIcon(bet.status)}
            <span className="uppercase">{bet.status}</span>
          </div>
        )}
      </div>
      
      {/* Runner Info */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-lg font-bold text-zinc-400">#{bet.runner_number || '?'}</span>
        <span className="text-sm font-medium text-zinc-100">{bet.runner_name}</span>
      </div>
      
      {/* Bet Details */}
      <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getBetTypeColor(bet.bet_type)}`}>
            {bet.bet_type === 'ew' ? 'E/W' : bet.bet_type || 'WIN'}
          </span>
          <span className="font-mono text-sm text-zinc-300">@{bet.odds_display || '?'}</span>
        </div>
        <div className="text-right">
          <p className="text-xs text-zinc-500">Stake</p>
          <p className="font-mono text-sm font-bold text-zinc-100">&euro;{bet.stake?.toFixed(2)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-zinc-500">Return</p>
          <p className="font-mono text-sm font-bold text-emerald-400">&euro;{bet.potential_payout?.toFixed(2)}</p>
        </div>
      </div>
    </div>
  );

  return (
    <TraderLayout>
      <div className="space-y-3 pb-20" data-testid="my-bets-page">
        {/* Race Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9" data-testid="race-filter">
              <SelectValue placeholder="All Races" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Races</SelectItem>
              {racesWithBets.map(race => (
                <SelectItem key={race.id} value={race.id}>
                  {race.venue} {race.time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-1.5">
          <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Bets</p>
            <p className="font-mono text-sm font-bold text-zinc-100">{filteredBets.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Staked</p>
            <p className="font-mono text-sm font-bold text-zinc-100">&euro;{totalStaked.toFixed(2)}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Won</p>
            <p className="font-mono text-sm font-bold text-emerald-400">&euro;{totalWon.toFixed(2)}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">P/L</p>
            <p className={`font-mono text-sm font-bold ${netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              &euro;{netProfit.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Bets Tabs */}
        <Tabs defaultValue="open" className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 p-1 grid grid-cols-4">
            <TabsTrigger 
              value="open" 
              className="data-[state=active]:bg-zinc-800 text-xs"
              data-testid="tab-open"
            >
              Open ({openBets.length})
            </TabsTrigger>
            <TabsTrigger 
              value="settled" 
              className="data-[state=active]:bg-zinc-800 text-xs"
              data-testid="tab-settled"
            >
              Settled ({settledBets.length})
            </TabsTrigger>
            <TabsTrigger 
              value="cancelled" 
              className="data-[state=active]:bg-zinc-800 text-xs"
              data-testid="tab-cancelled"
            >
              Cancelled ({cancelledBets.length})
            </TabsTrigger>
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-zinc-800 text-xs"
              data-testid="tab-all"
            >
              All ({filteredBets.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open" className="mt-3 space-y-2">
            {loading ? (
              <div className="space-y-2">
                {[1, 2].map(i => (
                  <div key={i} className="h-24 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
                ))}
              </div>
            ) : openBets.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No open bets</p>
              </div>
            ) : (
              openBets.map(bet => <BetCard key={bet.id} bet={bet} />)
            )}
          </TabsContent>

          <TabsContent value="settled" className="mt-3 space-y-2">
            {loading ? (
              <div className="space-y-2">
                {[1, 2].map(i => (
                  <div key={i} className="h-24 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
                ))}
              </div>
            ) : settledBets.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No settled bets</p>
              </div>
            ) : (
              settledBets.map(bet => <BetCard key={bet.id} bet={bet} />)
            )}
          </TabsContent>

          <TabsContent value="cancelled" className="mt-3 space-y-2">
            {cancelledBets.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Ban className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No cancelled bets</p>
              </div>
            ) : (
              cancelledBets.map(bet => <BetCard key={bet.id} bet={bet} />)
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-3 space-y-2">
            {loading ? (
              <div className="space-y-2">
                {[1, 2].map(i => (
                  <div key={i} className="h-24 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
                ))}
              </div>
            ) : filteredBets.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No bets placed yet</p>
              </div>
            ) : (
              filteredBets.map(bet => <BetCard key={bet.id} bet={bet} />)
            )}
          </TabsContent>
        </Tabs>
      </div>
    </TraderLayout>
  );
}
